/**
 * In-canvas uikit variant of KothHud.
 *
 * Same controller, same actions (start / touch / end), same tx-feedback strip.
 * Difference: rendered as WebGL meshes via @react-three/uikit inside the
 * R3F <Canvas>, so PostFX and scene lighting touch it and there's no DOM
 * overlay competing with the camera.
 *
 * Mounted in-world at a fixed position in the throne hall. The DOM KothHud
 * stays available — Lobby.tsx picks one or the other based on ?uikit=1.
 *
 * Honest gaps:
 *   - Clicks under pointer-lock still require a reticle/click-on-look system
 *     OR releasing the lock with Esc to drive the cursor. Not solved here.
 *   - On touch devices the DOM HUD is still better (large tap targets, top
 *     placement that doesn't fight the joystick); flag stays off for mobile.
 */
import { Container, Text } from "@react-three/uikit";

import type { KothLobbyController } from "./useKothLobbyController";
import type { KothRoundState } from "./useKothRound";

const KOTH_BASE_FEE_LAMPORTS = 1_000_000n;
const KOTH_STEP_FEE_LAMPORTS = 100_000n;
const KOTH_KEEPER_BOUNTY_LAMPORTS = 500_000n;
const LAMPORTS_PER_SOL = 1_000_000_000n;

const BG_PANEL = "#1a1208";
const BORDER_GOLD = "#8a6a28";
const ACCENT_GOLD = "#ffd66b";
const BUTTON_BG = "#4a8eff";
const BUTTON_BG_DISABLED = "#2a2a36";
const TEXT_LIGHT = "#f4ecd8";
const TEXT_MUTED = "#9aa0b4";
const DANGER = "#f4a4b4";

interface KothHudPanelProps {
  controller: KothLobbyController;
  position?: [number, number, number];
  rotation?: [number, number, number];
}

export function KothHudPanel({
  controller,
  position = [0, 3.0, 1.6],
  rotation = [0, 0, 0],
}: KothHudPanelProps): JSX.Element {
  const {
    round,
    roundId,
    walletConnected,
    expired,
    settled,
    txInFlight,
    txError,
    lastSig,
    pollError,
    startRound,
    touch,
    endRound,
    isYouKing,
  } = controller;

  const needsStart = !roundId || !round || settled;

  let buttonLabel: string;
  let buttonAction: (() => void) | null = null;
  let buttonDisabled = !walletConnected || txInFlight;

  if (!walletConnected) {
    buttonLabel = "Connect a wallet (top-right)";
  } else if (txInFlight) {
    buttonLabel = "Submitting…";
  } else if (needsStart) {
    buttonLabel = "Start round (10 min)";
    buttonAction = startRound;
  } else if (expired && round && !settled) {
    buttonLabel = `End round (+${lamportsToSolBigint(KOTH_KEEPER_BOUNTY_LAMPORTS)} SOL bounty)`;
    buttonAction = endRound;
  } else if (round && isYouKing) {
    // Self-touch is blocked on-chain — mirror that in the button so the king
    // can't fire a guaranteed-fail tx.
    buttonLabel = "👑 you hold the throne";
    buttonDisabled = true;
  } else if (round) {
    const fee = nextFeeLamports(round);
    const vigPct = readVigPct(round);
    const houseSuffix = vigPct > 0 ? ` (incl. ${vigPct}% house)` : "";
    buttonLabel = `TOUCH THE THRONE (${lamportsToSolBigint(fee)} SOL)${houseSuffix}`;
    buttonAction = touch;
  } else {
    buttonLabel = "—";
  }

  return (
    <group position={position} rotation={rotation}>
      <Container
        sizeX={2.6}
        flexDirection="column"
        gap={6}
        padding={14}
        backgroundColor={BG_PANEL}
        borderColor={BORDER_GOLD}
        borderWidth={2}
        borderRadius={10}
      >
        <Container justifyContent="center">
          <Text fontSize={18} fontWeight="bold" color={ACCENT_GOLD}>
            LAST TOUCH
          </Text>
        </Container>

        <Container
          justifyContent="center"
          alignItems="center"
          padding={10}
          backgroundColor={buttonDisabled ? BUTTON_BG_DISABLED : BUTTON_BG}
          opacity={buttonDisabled ? 0.7 : 1}
          borderRadius={6}
          onClick={() => {
            if (!buttonDisabled && buttonAction) buttonAction();
          }}
        >
          <Text fontSize={14} fontWeight="bold" color={TEXT_LIGHT}>
            {buttonLabel}
          </Text>
        </Container>

        {(pollError || txError || lastSig) && (
          <Container flexDirection="column" gap={2} paddingTop={4}>
            {pollError && (
              <Text fontSize={10} color={DANGER}>
                poll: {pollError}
              </Text>
            )}
            {txError && (
              <Text fontSize={10} color={DANGER}>
                tx: {txError}
              </Text>
            )}
            {lastSig && !txError && (
              <Text fontSize={10} color={TEXT_MUTED}>
                sig: {lastSig.slice(0, 12)}…
              </Text>
            )}
          </Container>
        )}
      </Container>
    </group>
  );
}

function lamportsToSolBigint(lamports: bigint): string {
  const whole = lamports / LAMPORTS_PER_SOL;
  const frac = lamports % LAMPORTS_PER_SOL;
  return `${whole}.${frac.toString().padStart(9, "0").slice(0, 6)}`;
}

function nextFeeLamports(round: KothRoundState): bigint {
  const snap = round.paramsSnapshot;
  const base = snap?.baseFeeLamports
    ? BigInt(snap.baseFeeLamports.toString())
    : KOTH_BASE_FEE_LAMPORTS;
  const step = snap?.stepFeeLamports
    ? BigInt(snap.stepFeeLamports.toString())
    : KOTH_STEP_FEE_LAMPORTS;
  const staticFee = base + step * BigInt(round.touchCount);
  const dynBps = snap?.dynamicFeeBps;
  if (!dynBps || dynBps <= 0) return staticFee;
  const pot = BigInt(round.potLamports.toString());
  const dynamicFloor = (pot * BigInt(dynBps)) / 10_000n;
  return staticFee > dynamicFloor ? staticFee : dynamicFloor;
}

function readVigPct(round: KothRoundState): number {
  // TODO: vig_bps from on-chain params once IDL ships — until then the field
  // is absent from paramsSnapshot, so we sentinel to 0 and suppress the suffix.
  const snap = round.paramsSnapshot as unknown as { vigBps?: number } | undefined;
  const bps = snap?.vigBps;
  if (typeof bps !== "number" || bps <= 0) return 0;
  return bps / 100;
}
